// AuthService.js - Real authentication service
const API_BASE_URL = '/api';

class AuthService {
  constructor() {
    this.token = localStorage.getItem('auth_token');
    this.user = this.token ? JSON.parse(localStorage.getItem('current_user') || 'null') : null;
  }

  // Set authentication headers
  getAuthHeaders() {
    if (this.token) {
      return {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json'
      };
    }
    return {
      'Content-Type': 'application/json'
    };
  }

  // Register new user
  async register(name, email, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name,
          email,
          password
        })
      });

      const data = await response.json();

      if (data.success && data.data) {
        this.token = data.data.token;
        this.user = data.data.user;
        
        localStorage.setItem('auth_token', this.token);
        localStorage.setItem('current_user', JSON.stringify(this.user));
        if (data.data.expiresAt) {
          localStorage.setItem('token_expires_at', data.data.expiresAt);
        }
        
        return { success: true, user: this.user };
      } else {
        return { success: false, message: data.message || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  }

  // Login user
  async login(email, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          password
        })
      });

      const data = await response.json();

      if (data.success && data.data) {
        this.token = data.data.token;
        this.user = data.data.user;
        
        localStorage.setItem('auth_token', this.token);
        localStorage.setItem('current_user', JSON.stringify(this.user));
        if (data.data.expiresAt) {
          localStorage.setItem('token_expires_at', data.data.expiresAt);
        }
        
        return { success: true, user: this.user };
      } else {
        return { success: false, message: data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  }

  // Logout user
  async logout() {
    try {
      if (this.token) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: this.getAuthHeaders()
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.token = null;
      this.user = null;
      localStorage.removeItem('auth_token');
      localStorage.removeItem('current_user');
      localStorage.removeItem('token_expires_at');
      localStorage.removeItem('user_progress');
    }
  }

  // Get current user info
  async getCurrentUser() {
    try {
      if (!this.token) {
        return { success: false, message: 'No token available' };
      }

      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: this.getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          this.user = result.data;
          localStorage.setItem('current_user', JSON.stringify(this.user));
          return { success: true, user: this.user };
        }
      }
      
      // Token might be expired
      this.logout();
      return { success: false, message: 'Authentication expired' };
    } catch (error) {
      console.error('Get current user error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    if (!this.token) return false;

    // Check token expiration
    const expiresAt = localStorage.getItem('token_expires_at');
    if (expiresAt && new Date(expiresAt) <= new Date()) {
      this.logout();
      return false;
    }

    return true;
  }

  // Get current user
  getUser() {
    return this.user;
  }

  // Get token
  getToken() {
    return this.token;
  }
}

export default new AuthService();